
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useNavigate, useParams } from "react-router-dom";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import api from "../../utills/api";

const PLATFORM_OPTIONS = ["Meta", "Google", "YouTube", "WhatsApp", "Website", "Other"];

const OBJECTIVE_OPTIONS = [
  "Lead Generation",
  "Admissions",
  "Brand Awareness",
  "Website Traffic",
  "Other",
];

const GOOGLE_IMAGE_TYPES = {
  landscape: {
    label: "Landscape",
    ratio: "1.91:1",
    recommended: "1200 × 628",
    minimum: "600 × 314",
    required: true,
    minCount: 1,
    recommendedCount: 4,
  },
  square: {
    label: "Square",
    ratio: "1:1",
    recommended: "1200 × 1200",
    minimum: "300 × 300",
    required: true,
    minCount: 1,
    recommendedCount: 4,
  },
  portrait: {
    label: "Portrait",
    ratio: "4:5",
    recommended: "960 × 1200",
    minimum: "480 × 600",
    required: false,
    minCount: 0,
    recommendedCount: 2,
  },
  logo: {
    label: "Square Logo",
    ratio: "1:1",
    recommended: "1200 × 1200",
    minimum: "128 × 128",
    required: true,
    minCount: 1,
    recommendedCount: 1,
  },
  landscapeLogo: {
    label: "Landscape Logo",
    ratio: "4:1",
    recommended: "1200 × 300",
    minimum: "512 × 128",
    required: false,
    minCount: 0,
    recommendedCount: 1,
  },
};

const initialForm = {
  name: "",
  code: "",
  platform: "Meta",
  objective: "Lead Generation",
  startDate: "",
  endDate: "",
  budget: "",
  targetAudience: "",
  landingPage: "",
  offer: "",

  google: {
    customerId: "",
    campaignType: "PERFORMANCE_MAX",
    leadFormAssetId: "",
    businessName: "",
    headlines: ["", "", ""],
    longHeadlines: [""],
    descriptions: ["", ""],
    selectedAssetIds: [],
    finalUrlExpansion: true,
    callToAction: "AUTOMATED",
  },

  meta: {
    adAccountId: "",
    pageId: "",
    instagramAccountId: "",
    leadFormId: "",
    primaryTexts: [""],
    headlines: [""],
    description: "",
    callToAction: "LEARN_MORE",
    selectedAssetIds: [],
  },

  youtube: {
    videoId: "",
    selectedAssetIds: [],
  },
};

const cloneInitialForm = () => JSON.parse(JSON.stringify(initialForm));

const safeArray = (value) => (Array.isArray(value) ? value : []);

const normalizeCampaignForEdit = (campaign) => {
  const next = cloneInitialForm();

  next.name = campaign?.name || "";
  next.code = campaign?.code || "";
  next.platform = campaign?.platform || "Meta";
  next.objective = campaign?.objective || "Lead Generation";
  next.startDate = campaign?.startDate
    ? new Date(campaign.startDate).toISOString().slice(0, 10)
    : "";
  next.endDate = campaign?.endDate
    ? new Date(campaign.endDate).toISOString().slice(0, 10)
    : "";
  next.budget =
    campaign?.budget !== undefined && campaign?.budget !== null
      ? String(campaign.budget)
      : "";
  next.targetAudience = campaign?.targetAudience || "";
  next.landingPage = campaign?.landingPage || "";
  next.offer = campaign?.offer || "";

  const config = campaign?.platformConfig || {};

  next.google = {
    ...next.google,
    ...(config.google || {}),
    customerId: config.google?.customerId || "",
    leadFormAssetId: config.google?.leadFormAssetId || "",
    headlines: safeArray(config.google?.headlines).length
      ? config.google.headlines
      : ["", "", ""],
    longHeadlines: safeArray(config.google?.longHeadlines).length
      ? config.google.longHeadlines
      : [""],
    descriptions: safeArray(config.google?.descriptions).length
      ? config.google.descriptions
      : ["", ""],
    selectedAssetIds: safeArray(config.google?.selectedAssetIds),
  };

  next.meta = {
    ...next.meta,
    ...(config.meta || {}),
    selectedAssetIds: safeArray(config.meta?.selectedAssetIds),
    primaryTexts: safeArray(config.meta?.primaryTexts).length
      ? config.meta.primaryTexts
      : [""],
    headlines: safeArray(config.meta?.headlines).length
      ? config.meta.headlines
      : [""],
  };

  next.youtube = {
    ...next.youtube,
    ...(config.youtube || {}),
    selectedAssetIds: safeArray(config.youtube?.selectedAssetIds),
  };

  return next;
};

const getFileDimensions = (file) =>
  new Promise((resolve) => {
    if (!file?.type?.startsWith("image/")) {
      resolve({ width: 0, height: 0 });
      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ width: 0, height: 0 });
    };

    img.src = url;
  });

const ratioOf = (width, height) => (height ? width / height : 0);

const ratioMatches = (width, height, expected, tolerance = 0.04) => {
  if (!width || !height) return false;
  const actual = ratioOf(width, height);
  return Math.abs(actual - expected) <= expected * tolerance;
};

const CampaignCreatePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const editing = Boolean(id);

  const [form, setForm] = useState(cloneInitialForm);
  const [step, setStep] = useState("details");

  const [googleData, setGoogleData] = useState({
    status: null,
    customers: [],
    leadForms: [],
  });

  const [metaData, setMetaData] = useState({
    status: null,
    pages: [],
    adAccounts: [],
    instagram: null,
  });

  const [youtubeData, setYoutubeData] = useState(null);

  const [assets, setAssets] = useState([]);
  const [assetLoading, setAssetLoading] = useState(false);

  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const updateField = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const updatePlatformField = (platform, field, value) => {
    setForm((previous) => ({
      ...previous,
      [platform]: {
        ...previous[platform],
        [field]: value,
      },
    }));
  };

  const fetchEditCampaign = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await api.get(`/campaigns/${id}`);
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || "Unable to fetch campaign.");
      }

      const campaign =
        response?.data?.data?.campaign ||
        response?.data?.data;

      setForm(normalizeCampaignForEdit(campaign));
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load campaign."
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEditCampaign();
  }, [fetchEditCampaign]);

  const loadGoogle = useCallback(async () => {
    try {
      const [statusRes, customersRes, formsRes] = await Promise.all([
        api.get("/google/status"),
        api.get("/google/customers"),
        api.get("/google/lead-forms"),
      ]);

      setGoogleData({
        status: statusRes?.data?.data || null,
        customers: customersRes?.data?.data?.results || [],
        leadForms: formsRes?.data?.data?.results || [],
      });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load Google campaign options."
      );
    }
  }, []);

  const loadMeta = useCallback(async () => {
    try {
      const [statusRes, pagesRes, adAccountsRes, instagramRes] =
        await Promise.all([
          api.get("/meta/status"),
          api.get("/meta/pages"),
          api.get("/meta/ad-accounts"),
          api.get("/meta/instagram"),
        ]);

      setMetaData({
        status: statusRes?.data?.data || null,
        pages: pagesRes?.data?.data || [],
        adAccounts: adAccountsRes?.data?.data || [],
        instagram: instagramRes?.data?.data || null,
      });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load Meta campaign options."
      );
    }
  }, []);

  const loadYouTube = useCallback(async () => {
    try {
      const response = await api.get("/youtube/status");
      setYoutubeData(response?.data?.data || null);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to load YouTube campaign options."
      );
    }
  }, []);

  useEffect(() => {
    if (form.platform === "Google") loadGoogle();
    if (form.platform === "Meta") loadMeta();
    if (form.platform === "YouTube") loadYouTube();
  }, [form.platform, loadGoogle, loadMeta, loadYouTube]);

  const loadAssets = useCallback(async () => {
    try {
      setAssetLoading(true);

      // Expected backend endpoint:
      // GET /marketing/assets?platform=Google|Meta|YouTube
      // The current project did not yet expose this route, so a 404 is handled gracefully.
      const response = await api.get("/marketing/assets", {
        params: { platform: form.platform },
      });

      const list =
        response?.data?.data?.assets ||
        response?.data?.data ||
        [];

      setAssets(Array.isArray(list) ? list : []);
    } catch (err) {
      setAssets([]);
      if (err?.response?.status !== 404) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load marketing assets."
        );
      }
    } finally {
      setAssetLoading(false);
    }
  }, [form.platform]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  const addLocalAssets = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    const incoming = [];

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        setError(`${file.name}: maximum supported size is 5 MB.`);
        continue;
      }

      if (!["image/jpeg", "image/png", "image/gif"].includes(file.type)) {
        setError(`${file.name}: only JPG, PNG or GIF image files are supported here.`);
        continue;
      }

      const dimensions = await getFileDimensions(file);
      incoming.push({
        id: `local-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        name: file.name,
        type: "IMAGE",
        platform: form.platform,
        width: dimensions.width,
        height: dimensions.height,
        size: file.size,
        previewUrl: URL.createObjectURL(file),
        file,
        local: true,
      });
    }

    setAssets((previous) => [...incoming, ...previous]);
    event.target.value = "";
  };

  const platformAssetIds = useMemo(() => {
    if (form.platform === "Google") return form.google.selectedAssetIds;
    if (form.platform === "Meta") return form.meta.selectedAssetIds;
    if (form.platform === "YouTube") return form.youtube.selectedAssetIds;
    return [];
  }, [form]);

  const toggleAsset = (assetId) => {
    const currentIds = platformAssetIds || [];
    const nextIds = currentIds.includes(assetId)
      ? currentIds.filter((item) => item !== assetId)
      : [...currentIds, assetId];

    if (form.platform === "Google") {
      updatePlatformField("google", "selectedAssetIds", nextIds);
    } else if (form.platform === "Meta") {
      updatePlatformField("meta", "selectedAssetIds", nextIds);
    } else if (form.platform === "YouTube") {
      updatePlatformField("youtube", "selectedAssetIds", nextIds);
    }
  };

  const filteredAssets = useMemo(() => {
    return assets.filter((asset) => {
      if (!asset) return false;
      if (asset.platform && asset.platform !== "Universal" && asset.platform !== form.platform) {
        return false;
      }
      return true;
    });
  }, [assets, form.platform]);

  const googleCounts = useMemo(() => {
    const selected = assets.filter((asset) =>
      form.google.selectedAssetIds.includes(asset.id || asset._id)
    );

    return {
      landscape: selected.filter((asset) =>
        ratioMatches(asset.width, asset.height, 1.91)
      ).length,
      square: selected.filter((asset) =>
        ratioMatches(asset.width, asset.height, 1)
      ).length,
      portrait: selected.filter((asset) =>
        ratioMatches(asset.width, asset.height, 0.8)
      ).length,
      logo: selected.filter((asset) =>
        asset.assetCategory === "LOGO" ||
        asset.type === "LOGO"
      ).length,
    };
  }, [assets, form.google.selectedAssetIds]);

  const validation = useMemo(() => {
    const issues = [];

    if (!form.name.trim()) issues.push("Campaign name is required.");
    if (!form.startDate) issues.push("Start date is required.");
    if (!form.endDate) issues.push("End date is required.");
    if (form.endDate && form.startDate && form.endDate < form.startDate) {
      issues.push("End date cannot be before start date.");
    }
    if (Number(form.budget || 0) <= 0) issues.push("Budget must be greater than zero.");
    if (!form.landingPage.trim()) issues.push("Landing page is required.");

    if (form.platform === "Google") {
      const google = form.google;

      if (!google.customerId) issues.push("Google Ads customer is required.");
      if (!google.leadFormAssetId) issues.push("Google Lead Form is required.");
      if (!google.businessName.trim()) issues.push("Google Business Name is required.");

      if (google.headlines.filter((x) => x.trim()).length < 3) {
        issues.push("Google needs at least 3 headlines.");
      }
      if (google.longHeadlines.filter((x) => x.trim()).length < 1) {
        issues.push("Google needs at least 1 long headline.");
      }
      if (google.descriptions.filter((x) => x.trim()).length < 2) {
        issues.push("Google needs at least 2 descriptions.");
      }

      if (googleCounts.landscape < 1) {
        issues.push("Select at least 1 Google landscape image.");
      }
      if (googleCounts.square < 1) {
        issues.push("Select at least 1 Google square image.");
      }
      if (googleCounts.logo < 1) {
        issues.push("Select at least 1 Google logo asset.");
      }
    }

    if (form.platform === "Meta") {
      const meta = form.meta;

      if (!meta.adAccountId) issues.push("Meta ad account is required.");
      if (!meta.pageId) issues.push("Facebook Page is required.");
      if (!meta.leadFormId.trim()) issues.push("Meta Lead Form ID is required.");

      if (!meta.primaryTexts.some((x) => x.trim())) {
        issues.push("At least one Meta primary text is required.");
      }

      if (!meta.headlines.some((x) => x.trim())) {
        issues.push("At least one Meta headline is required.");
      }
    }

    if (form.platform === "YouTube") {
      if (!youtubeData?.connected) issues.push("YouTube must be connected.");
    }

    return issues;
  }, [form, googleCounts, youtubeData]);

  const handleTextArrayChange = (platform, field, index, value) => {
    setForm((previous) => ({
      ...previous,
      [platform]: {
        ...previous[platform],
        [field]: previous[platform][field].map((item, itemIndex) =>
          itemIndex === index ? value : item
        ),
      },
    }));
  };

  const addTextArrayItem = (platform, field) => {
    setForm((previous) => ({
      ...previous,
      [platform]: {
        ...previous[platform],
        [field]: [...previous[platform][field], ""],
      },
    }));
  };

  const removeTextArrayItem = (platform, field, index) => {
    setForm((previous) => ({
      ...previous,
      [platform]: {
        ...previous[platform],
        [field]: previous[platform][field].filter(
          (_, itemIndex) => itemIndex !== index
        ),
      },
    }));
  };

  const handleSave = async (event) => {
    event.preventDefault();

    setError("");
    setSuccess("");

    if (validation.length) {
      setError(validation[0]);
      setStep("details");
      return;
    }

    try {
      setSaving(true);

      const payload = {
        name: form.name.trim(),
        code: form.code.trim() || undefined,
        platform: form.platform,
        objective: form.objective,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        budget: Number(form.budget || 0),
        targetAudience: form.targetAudience.trim() || undefined,
        landingPage: form.landingPage.trim() || undefined,
        offer: form.offer.trim() || undefined,

        /*
         * Backend contract for this UI.
         * These fields should be persisted by Campaign schema/backend.
         */
        platformConfig: {
          google:
            form.platform === "Google"
              ? {
                  ...form.google,
                  selectedAssetIds: form.google.selectedAssetIds,
                }
              : undefined,

          meta:
            form.platform === "Meta"
              ? {
                  ...form.meta,
                  selectedAssetIds: form.meta.selectedAssetIds,
                }
              : undefined,

          youtube:
            form.platform === "YouTube"
              ? form.youtube
              : undefined,
        },
      };

      let response;

      if (editing) {
        response = await api.put(`/campaigns/${id}`, payload);
      } else {
        response = await api.post("/campaigns", payload);
      }

      if (!response?.data?.success) {
        throw new Error(
          response?.data?.message ||
            "Unable to save campaign."
        );
      }

      setSuccess(
        editing
          ? "Campaign updated successfully."
          : "Campaign created successfully."
      );

      const savedId =
        response?.data?.data?.campaign?._id ||
        response?.data?.data?.campaign?.id ||
        response?.data?.data?._id ||
        response?.data?.data?.id;

      if (savedId) {
        navigate(`/campaigns/${savedId}`);
      } else {
        navigate("/campaigns");
      }
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to save campaign."
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedAssetCount = platformAssetIds.length;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={editing ? "Edit Campaign" : "Create Campaign"}
          pageTitle="Marketing"
        />

        {error && (
          <Alert color="danger" toggle={() => setError("")}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert color="success" toggle={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        {loading ? (
          <Card>
            <CardBody className="text-center py-5">
              <Spinner size="sm" className="me-2" />
              Loading campaign...
            </CardBody>
          </Card>
        ) : (
          <Form onSubmit={handleSave}>
            <Row className="g-3">
              <Col xl={9}>
                <Card>
                  <CardBody>
                    <div className="d-flex align-items-center justify-content-between mb-4">
                      <div>
                        <h4 className="mb-1">
                          {editing ? "Edit Campaign" : "Create Campaign"}
                        </h4>
                        <p className="text-muted mb-0">
                          Build one campaign and configure only the assets/settings
                          needed by its selected platform.
                        </p>
                      </div>

                      <Badge color="primary">
                        {form.platform}
                      </Badge>
                    </div>

                    <Nav tabs className="mb-4">
                      <NavItem>
                        <NavLink
                          role="button"
                          className={step === "details" ? "active" : ""}
                          onClick={() => setStep("details")}
                        >
                          1. Campaign
                        </NavLink>
                      </NavItem>

                      <NavItem>
                        <NavLink
                          role="button"
                          className={step === "platform" ? "active" : ""}
                          onClick={() => setStep("platform")}
                        >
                          2. Platform
                        </NavLink>
                      </NavItem>

                      <NavItem>
                        <NavLink
                          role="button"
                          className={step === "assets" ? "active" : ""}
                          onClick={() => setStep("assets")}
                        >
                          3. Assets
                        </NavLink>
                      </NavItem>

                      <NavItem>
                        <NavLink
                          role="button"
                          className={step === "review" ? "active" : ""}
                          onClick={() => setStep("review")}
                        >
                          4. Review
                        </NavLink>
                      </NavItem>
                    </Nav>

                    {step === "details" && (
                      <Row className="g-3">
                        <Col md={8}>
                          <Label>Campaign Name *</Label>
                          <Input
                            value={form.name}
                            onChange={(e) => updateField("name", e.target.value)}
                            placeholder="e.g. 2026 Admissions Campaign"
                          />
                        </Col>

                        <Col md={4}>
                          <Label>Campaign Code</Label>
                          <Input
                            value={form.code}
                            onChange={(e) => updateField("code", e.target.value)}
                            placeholder="e.g. ADM2026"
                          />
                        </Col>

                        <Col md={6}>
                          <Label>Platform *</Label>
                          <Input
                            type="select"
                            value={form.platform}
                            onChange={(e) =>
                              updateField("platform", e.target.value)
                            }
                          >
                            {PLATFORM_OPTIONS.map((item) => (
                              <option key={item} value={item}>
                                {item}
                              </option>
                            ))}
                          </Input>
                        </Col>

                        <Col md={6}>
                          <Label>Objective *</Label>
                          <Input
                            type="select"
                            value={form.objective}
                            onChange={(e) =>
                              updateField("objective", e.target.value)
                            }
                          >
                            {OBJECTIVE_OPTIONS.map((item) => (
                              <option key={item} value={item}>
                                {item}
                              </option>
                            ))}
                          </Input>
                        </Col>

                        <Col md={6}>
                          <Label>Start Date *</Label>
                          <Input
                            type="date"
                            value={form.startDate}
                            onChange={(e) =>
                              updateField("startDate", e.target.value)
                            }
                          />
                        </Col>

                        <Col md={6}>
                          <Label>End Date *</Label>
                          <Input
                            type="date"
                            value={form.endDate}
                            onChange={(e) =>
                              updateField("endDate", e.target.value)
                            }
                          />
                        </Col>

                        <Col md={6}>
                          <Label>Budget *</Label>
                          <Input
                            type="number"
                            min="1"
                            value={form.budget}
                            onChange={(e) =>
                              updateField("budget", e.target.value)
                            }
                            placeholder="5000"
                          />
                        </Col>

                        <Col md={6}>
                          <Label>Landing Page *</Label>
                          <Input
                            type="url"
                            value={form.landingPage}
                            onChange={(e) =>
                              updateField("landingPage", e.target.value)
                            }
                            placeholder="https://www.example.com/admissions"
                          />
                        </Col>

                        <Col md={12}>
                          <Label>Target Audience</Label>
                          <Input
                            type="textarea"
                            rows={3}
                            value={form.targetAudience}
                            onChange={(e) =>
                              updateField("targetAudience", e.target.value)
                            }
                            placeholder="Students, parents, graduates, etc."
                          />
                        </Col>

                        <Col md={12}>
                          <Label>Offer</Label>
                          <Input
                            type="textarea"
                            rows={2}
                            value={form.offer}
                            onChange={(e) =>
                              updateField("offer", e.target.value)
                            }
                            placeholder="Scholarship, early admission offer, counselling offer..."
                          />
                        </Col>

                        <Col md={12} className="text-end">
                          <Button
                            color="primary"
                            type="button"
                            onClick={() => setStep("platform")}
                          >
                            Continue to {form.platform}
                            <i className="ri-arrow-right-line ms-1" />
                          </Button>
                        </Col>
                      </Row>
                    )}

                    {step === "platform" && form.platform === "Google" && (
                      <GoogleSection
                        form={form.google}
                        data={googleData}
                        updateField={updatePlatformField}
                        updateArray={handleTextArrayChange}
                        addArray={addTextArrayItem}
                        removeArray={removeTextArrayItem}
                        onNext={() => setStep("assets")}
                      />
                    )}

                    {step === "platform" && form.platform === "Meta" && (
                      <MetaSection
                        form={form.meta}
                        data={metaData}
                        updateField={updatePlatformField}
                        updateArray={handleTextArrayChange}
                        addArray={addTextArrayItem}
                        removeArray={removeTextArrayItem}
                        onNext={() => setStep("assets")}
                      />
                    )}

                    {step === "platform" && form.platform === "YouTube" && (
                      <YouTubeSection
                        form={form.youtube}
                        data={youtubeData}
                        updateField={updatePlatformField}
                        onNext={() => setStep("assets")}
                      />
                    )}

                    {step === "platform" &&
                      !["Google", "Meta", "YouTube"].includes(form.platform) && (
                        <Alert color="info">
                          No additional platform-specific settings are required
                          for {form.platform}. Continue to the asset step.
                        </Alert>
                      )}

                    {step === "platform" && (
                      <div className="d-flex justify-content-between mt-4">
                        <Button
                          type="button"
                          color="light"
                          onClick={() => setStep("details")}
                        >
                          Back
                        </Button>

                        <Button
                          type="button"
                          color="primary"
                          onClick={() => setStep("assets")}
                        >
                          Continue to Assets
                        </Button>
                      </div>
                    )}

                    {step === "assets" && (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div>
                            <h5 className="mb-1">
                              {form.platform} Creative Assets
                            </h5>
                            <p className="text-muted mb-0">
                              Select reusable assets. Upload them to the asset
                              library once and reuse them across campaigns.
                            </p>
                          </div>

                          <Badge color="info">
                            {selectedAssetCount} selected
                          </Badge>
                        </div>

                        {form.platform === "Google" && (
                          <GoogleAssetRequirements counts={googleCounts} />
                        )}

                        <Row className="g-3 mb-4">
                          <Col md={8}>
                            <Label>Upload new image to Asset Library</Label>
                            <Input
                              type="file"
                              accept=".jpg,.jpeg,.png,.gif"
                              multiple
                              onChange={addLocalAssets}
                            />
                            <div className="form-text">
                              New files are previewed locally here. Persisting
                              them as reusable library records requires the
                              marketing-assets backend endpoint described in
                              README.md.
                            </div>
                          </Col>

                          <Col md={4} className="d-flex align-items-end">
                            <Button
                              type="button"
                              color="light"
                              className="w-100"
                              onClick={loadAssets}
                              disabled={assetLoading}
                            >
                              {assetLoading && (
                                <Spinner size="sm" className="me-2" />
                              )}
                              Refresh Asset Library
                            </Button>
                          </Col>
                        </Row>

                        {!filteredAssets.length ? (
                          <Alert color="warning">
                            No reusable assets were returned from the asset
                            library. You can still upload local files to preview
                            and select them, but they must be persisted by the
                            asset-library backend before they can be reused by
                            other campaigns.
                          </Alert>
                        ) : (
                          <Row className="g-3">
                            {filteredAssets.map((asset) => {
                              const assetId = asset.id || asset._id;
                              const selected =
                                platformAssetIds.includes(assetId);

                              return (
                                <Col xl={3} md={4} sm={6} key={assetId}>
                                  <Card
                                    className={`h-100 mb-0 ${
                                      selected ? "border-primary shadow-sm" : ""
                                    }`}
                                  >
                                    <CardBody>
                                      {asset.previewUrl ? (
                                        <img
                                          src={asset.previewUrl}
                                          alt={asset.name || "asset"}
                                          className="w-100 rounded mb-3"
                                          style={{
                                            height: 150,
                                            objectFit: "cover",
                                          }}
                                        />
                                      ) : (
                                        <div
                                          className="bg-light rounded mb-3 d-flex align-items-center justify-content-center"
                                          style={{ height: 150 }}
                                        >
                                          <i className="ri-image-line fs-28 text-muted" />
                                        </div>
                                      )}

                                      <div className="fw-semibold text-truncate">
                                        {asset.name || "Unnamed asset"}
                                      </div>

                                      <div className="text-muted fs-12 mt-1">
                                        {asset.width || "-"} × {asset.height || "-"}
                                      </div>

                                      <Button
                                        type="button"
                                        size="sm"
                                        color={selected ? "primary" : "light"}
                                        className="w-100 mt-3"
                                        onClick={() => toggleAsset(assetId)}
                                      >
                                        {selected ? "Selected" : "Select Asset"}
                                      </Button>
                                    </CardBody>
                                  </Card>
                                </Col>
                              );
                            })}
                          </Row>
                        )}

                        <div className="d-flex justify-content-between mt-4">
                          <Button
                            type="button"
                            color="light"
                            onClick={() => setStep("platform")}
                          >
                            Back
                          </Button>

                          <Button
                            type="button"
                            color="primary"
                            onClick={() => setStep("review")}
                          >
                            Continue to Review
                          </Button>
                        </div>
                      </>
                    )}

                    {step === "review" && (
                      <>
                        <ReviewSection
                          form={form}
                          selectedAssetCount={selectedAssetCount}
                          googleCounts={googleCounts}
                        />

                        <div className="d-flex justify-content-between mt-4">
                          <Button
                            type="button"
                            color="light"
                            onClick={() => setStep("assets")}
                          >
                            Back
                          </Button>

                          <Button
                            type="submit"
                            color="primary"
                            disabled={saving || validation.length > 0}
                          >
                            {saving && (
                              <Spinner size="sm" className="me-2" />
                            )}
                            {editing ? "Update Campaign" : "Create Campaign"}
                          </Button>
                        </div>
                      </>
                    )}
                  </CardBody>
                </Card>
              </Col>

              <Col xl={3}>
                <Card className="sticky-top" style={{ top: 90 }}>
                  <CardBody>
                    <h5 className="mb-3">Campaign Readiness</h5>

                    <ReadinessItem
                      label="Basic campaign"
                      ok={
                        Boolean(form.name.trim()) &&
                        Boolean(form.startDate) &&
                        Boolean(form.endDate) &&
                        Number(form.budget || 0) > 0 &&
                        Boolean(form.landingPage.trim())
                      }
                    />

                    <ReadinessItem
                      label={`${form.platform} configuration`}
                      ok={
                        form.platform === "Google"
                          ? Boolean(form.google.customerId) &&
                            Boolean(form.google.leadFormAssetId)
                          : form.platform === "Meta"
                          ? Boolean(form.meta.adAccountId) &&
                            Boolean(form.meta.pageId)
                          : form.platform === "YouTube"
                          ? Boolean(youtubeData?.connected)
                          : true
                      }
                    />

                    <ReadinessItem
                      label="Creative assets"
                      ok={
                        form.platform === "Google"
                          ? googleCounts.landscape >= 1 &&
                            googleCounts.square >= 1 &&
                            googleCounts.logo >= 1
                          : selectedAssetCount > 0 || form.platform === "YouTube"
                      }
                    />

                    <hr />

                    <div className="small text-muted mb-2">
                      Platform
                    </div>
                    <div className="fw-semibold mb-3">
                      {form.platform}
                    </div>

                    <div className="small text-muted mb-2">
                      Objective
                    </div>
                    <div className="fw-semibold mb-3">
                      {form.objective}
                    </div>

                    <div className="small text-muted mb-2">
                      Budget
                    </div>
                    <div className="fw-semibold mb-3">
                      ₹{Number(form.budget || 0).toLocaleString("en-IN")}
                    </div>

                    <div className="small text-muted mb-2">
                      Assets selected
                    </div>
                    <Badge color="primary">
                      {selectedAssetCount}
                    </Badge>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Form>
        )}
      </Container>
    </div>
  );
};

const GoogleSection = ({
  form,
  data,
  updateField,
  updateArray,
  addArray,
  removeArray,
  onNext,
}) => {
  const customers = data?.customers || [];
  const leadForms = data?.leadForms || [];

  return (
    <Row className="g-3">
      <Col md={6}>
        <Label>Google Ads Customer *</Label>
        <Input
          type="select"
          value={form.customerId}
          onChange={(e) =>
            updateField("google", "customerId", e.target.value)
          }
        >
          <option value="">Select Google Ads account</option>
          {customers.map((item) => {
            const customerId =
              item?.customerClient?.id ||
              item?.customer?.id ||
              item?.id ||
              "";

            const name =
              item?.customerClient?.descriptiveName ||
              item?.customer?.descriptiveName ||
              item?.descriptiveName ||
              customerId;

            return (
              <option key={customerId} value={customerId}>
                {name} — {customerId}
              </option>
            );
          })}
        </Input>
      </Col>

      <Col md={6}>
        <Label>Campaign Type</Label>
        <Input type="select" value={form.campaignType} disabled>
          <option value="PERFORMANCE_MAX">Performance Max</option>
        </Input>
      </Col>

      <Col md={8}>
        <Label>Google Lead Form *</Label>
        <Input
          type="select"
          value={form.leadFormAssetId}
          onChange={(e) =>
            updateField("google", "leadFormAssetId", e.target.value)
          }
        >
          <option value="">Select existing Lead Form</option>
          {leadForms.map((item) => {
            const asset = item?.asset || {};
            return (
              <option
                key={asset.resourceName || asset.id}
                value={asset.resourceName || asset.id}
              >
                {asset.name || `Lead Form ${asset.id || ""}`}
              </option>
            );
          })}
        </Input>
      </Col>

      <Col md={4}>
        <Label>Business Name *</Label>
        <Input
          maxLength={25}
          value={form.businessName}
          onChange={(e) =>
            updateField("google", "businessName", e.target.value)
          }
          placeholder="YatraNest"
        />
      </Col>

      <Col md={12}>
        <div className="border rounded p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div>
              <h6 className="mb-1">Google Text Assets</h6>
              <div className="text-muted fs-12">
                Minimum: 3 headlines, 1 long headline, 2 descriptions.
              </div>
            </div>
            <Badge color="info">
              PMax
            </Badge>
          </div>

          <Row className="g-3">
            <Col md={12}>
              <Label>Headlines</Label>
              {form.headlines.map((value, index) => (
                <div className="d-flex gap-2 mb-2" key={`headline-${index}`}>
                  <Input
                    maxLength={30}
                    value={value}
                    onChange={(e) =>
                      updateArray(
                        "google",
                        "headlines",
                        index,
                        e.target.value
                      )
                    }
                    placeholder={`Headline ${index + 1}`}
                  />
                  {form.headlines.length > 3 && (
                    <Button
                      type="button"
                      color="light"
                      onClick={() =>
                        removeArray("google", "headlines", index)
                      }
                    >
                      <i className="ri-delete-bin-line text-danger" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                size="sm"
                color="light"
                onClick={() => addArray("google", "headlines")}
              >
                + Add headline
              </Button>
            </Col>

            <Col md={12}>
              <Label>Long Headlines</Label>
              {form.longHeadlines.map((value, index) => (
                <div className="d-flex gap-2 mb-2" key={`long-${index}`}>
                  <Input
                    maxLength={90}
                    value={value}
                    onChange={(e) =>
                      updateArray(
                        "google",
                        "longHeadlines",
                        index,
                        e.target.value
                      )
                    }
                    placeholder={`Long headline ${index + 1}`}
                  />
                  {form.longHeadlines.length > 1 && (
                    <Button
                      type="button"
                      color="light"
                      onClick={() =>
                        removeArray("google", "longHeadlines", index)
                      }
                    >
                      <i className="ri-delete-bin-line text-danger" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                size="sm"
                color="light"
                onClick={() => addArray("google", "longHeadlines")}
              >
                + Add long headline
              </Button>
            </Col>

            <Col md={12}>
              <Label>Descriptions</Label>
              {form.descriptions.map((value, index) => (
                <div className="d-flex gap-2 mb-2" key={`description-${index}`}>
                  <Input
                    maxLength={90}
                    value={value}
                    onChange={(e) =>
                      updateArray(
                        "google",
                        "descriptions",
                        index,
                        e.target.value
                      )
                    }
                    placeholder={`Description ${index + 1}`}
                  />
                  {form.descriptions.length > 2 && (
                    <Button
                      type="button"
                      color="light"
                      onClick={() =>
                        removeArray("google", "descriptions", index)
                      }
                    >
                      <i className="ri-delete-bin-line text-danger" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                size="sm"
                color="light"
                onClick={() => addArray("google", "descriptions")}
              >
                + Add description
              </Button>
            </Col>
          </Row>
        </div>
      </Col>

      <Col md={6}>
        <Label>Final URL Expansion</Label>
        <Input
          type="select"
          value={form.finalUrlExpansion ? "true" : "false"}
          onChange={(e) =>
            updateField(
              "google",
              "finalUrlExpansion",
              e.target.value === "true"
            )
          }
        >
          <option value="true">Enabled</option>
          <option value="false">Disabled</option>
        </Input>
      </Col>

      <Col md={6}>
        <Label>Call to Action</Label>
        <Input
          type="select"
          value={form.callToAction}
          onChange={(e) =>
            updateField("google", "callToAction", e.target.value)
          }
        >
          <option value="AUTOMATED">Automated</option>
          <option value="LEARN_MORE">Learn More</option>
          <option value="SIGN_UP">Sign Up</option>
          <option value="GET_QUOTE">Get Quote</option>
          <option value="CONTACT_US">Contact Us</option>
        </Input>
      </Col>

      <Col md={12} className="text-end">
        <Button type="button" color="primary" onClick={onNext}>
          Continue to Assets
        </Button>
      </Col>
    </Row>
  );
};

const MetaSection = ({
  form,
  data,
  updateField,
  updateArray,
  addArray,
  removeArray,
  onNext,
}) => (
  <Row className="g-3">
    <Col md={6}>
      <Label>Meta Ad Account *</Label>
      <Input
        type="select"
        value={form.adAccountId}
        onChange={(e) =>
          updateField("meta", "adAccountId", e.target.value)
        }
      >
        <option value="">Select ad account</option>
        {(data?.adAccounts || []).map((item) => (
          <option
            key={item.id || item.accountId}
            value={item.id || item.accountId}
          >
            {item.name} — {item.id || item.accountId}
          </option>
        ))}
      </Input>
    </Col>

    <Col md={6}>
      <Label>Facebook Page *</Label>
      <Input
        type="select"
        value={form.pageId}
        onChange={(e) =>
          updateField("meta", "pageId", e.target.value)
        }
      >
        <option value="">Select Page</option>
        {(data?.pages || []).map((page) => (
          <option key={page.id} value={page.id}>
            {page.name} — {page.id}
          </option>
        ))}
      </Input>
    </Col>

    <Col md={6}>
      <Label>Instagram Account</Label>
      <Input
        value={form.instagramAccountId}
        onChange={(e) =>
          updateField("meta", "instagramAccountId", e.target.value)
        }
        placeholder="Connected Instagram account ID"
      />
      <div className="form-text">
        Your existing Meta integration can supply the connected Instagram ID.
      </div>
    </Col>

    <Col md={6}>
      <Label>Meta Lead Form ID *</Label>
      <Input
        value={form.leadFormId}
        onChange={(e) =>
          updateField("meta", "leadFormId", e.target.value)
        }
        placeholder="e.g. 1275184998025527"
      />
    </Col>

    <Col md={12}>
      <div className="border rounded p-3">
        <h6>Meta Ad Copy</h6>

        <Label>Primary Text</Label>
        {form.primaryTexts.map((value, index) => (
          <div className="d-flex gap-2 mb-2" key={`primary-${index}`}>
            <Input
              type="textarea"
              rows={2}
              value={value}
              onChange={(e) =>
                updateArray(
                  "meta",
                  "primaryTexts",
                  index,
                  e.target.value
                )
              }
              placeholder={`Primary text ${index + 1}`}
            />
            {form.primaryTexts.length > 1 && (
              <Button
                type="button"
                color="light"
                onClick={() =>
                  removeArray("meta", "primaryTexts", index)
                }
              >
                <i className="ri-delete-bin-line text-danger" />
              </Button>
            )}
          </div>
        ))}

        <Button
          type="button"
          size="sm"
          color="light"
          onClick={() => addArray("meta", "primaryTexts")}
        >
          + Add primary text
        </Button>

        <div className="mt-3">
          <Label>Headline</Label>
          {form.headlines.map((value, index) => (
            <div className="d-flex gap-2 mb-2" key={`meta-headline-${index}`}>
              <Input
                value={value}
                onChange={(e) =>
                  updateArray(
                    "meta",
                    "headlines",
                    index,
                    e.target.value
                  )
                }
                placeholder={`Headline ${index + 1}`}
              />
              {form.headlines.length > 1 && (
                <Button
                  type="button"
                  color="light"
                  onClick={() =>
                    removeArray("meta", "headlines", index)
                  }
                >
                  <i className="ri-delete-bin-line text-danger" />
                </Button>
              )}
            </div>
          ))}

          <Button
            type="button"
            size="sm"
            color="light"
            onClick={() => addArray("meta", "headlines")}
          >
            + Add headline
          </Button>
        </div>

        <div className="mt-3">
          <Label>Description</Label>
          <Input
            type="textarea"
            rows={2}
            value={form.description}
            onChange={(e) =>
              updateField("meta", "description", e.target.value)
            }
          />
        </div>

        <div className="mt-3">
          <Label>Call to Action</Label>
          <Input
            type="select"
            value={form.callToAction}
            onChange={(e) =>
              updateField("meta", "callToAction", e.target.value)
            }
          >
            <option value="LEARN_MORE">Learn More</option>
            <option value="SIGN_UP">Sign Up</option>
            <option value="CONTACT_US">Contact Us</option>
            <option value="GET_QUOTE">Get Quote</option>
          </Input>
        </div>
      </div>
    </Col>

    <Col md={12} className="text-end">
      <Button type="button" color="primary" onClick={onNext}>
        Continue to Assets
      </Button>
    </Col>
  </Row>
);

const YouTubeSection = ({ form, data, updateField }) => (
  <Row className="g-3">
    <Col md={12}>
      <Alert color={data?.connected ? "success" : "warning"}>
        {data?.connected
          ? `Connected to YouTube channel: ${data?.channel?.name || "-"}`
          : "YouTube is not currently connected."}
      </Alert>
    </Col>

    <Col md={8}>
      <Label>YouTube Video ID</Label>
      <Input
        value={form.videoId}
        onChange={(e) =>
          updateField("youtube", "videoId", e.target.value)
        }
        placeholder="Optional: YouTube video ID"
      />
    </Col>

    <Col md={4}>
      <Label>Attribution</Label>
      <Input value="youtube / video" disabled />
    </Col>
  </Row>
);

const GoogleAssetRequirements = ({ counts }) => (
  <Alert color="light" className="border">
    <div className="fw-semibold mb-2">Google Performance Max asset checklist</div>
    <div className="d-flex flex-wrap gap-3 small">
      <span>
        Landscape:{" "}
        <Badge color={counts.landscape >= 1 ? "success" : "danger"}>
          {counts.landscape}
        </Badge>
      </span>
      <span>
        Square:{" "}
        <Badge color={counts.square >= 1 ? "success" : "danger"}>
          {counts.square}
        </Badge>
      </span>
      <span>
        Portrait:{" "}
        <Badge color="secondary">{counts.portrait}</Badge>
      </span>
      <span>
        Logo:{" "}
        <Badge color={counts.logo >= 1 ? "success" : "danger"}>
          {counts.logo}
        </Badge>
      </span>
    </div>
  </Alert>
);

const ReadinessItem = ({ label, ok }) => (
  <div className="d-flex align-items-center justify-content-between mb-3">
    <span>{label}</span>
    {ok ? (
      <Badge color="success">Ready</Badge>
    ) : (
      <Badge color="warning">Needs setup</Badge>
    )}
  </div>
);

const ReviewSection = ({ form, selectedAssetCount, googleCounts }) => (
  <div>
    <h5 className="mb-3">Campaign Review</h5>

    <Table responsive bordered className="align-middle">
      <tbody>
        <tr>
          <th style={{ width: 220 }}>Campaign</th>
          <td>{form.name || "-"}</td>
        </tr>
        <tr>
          <th>Code</th>
          <td>{form.code || "-"}</td>
        </tr>
        <tr>
          <th>Platform</th>
          <td>
            <Badge color="primary">{form.platform}</Badge>
          </td>
        </tr>
        <tr>
          <th>Objective</th>
          <td>{form.objective}</td>
        </tr>
        <tr>
          <th>Dates</th>
          <td>
            {form.startDate || "-"} → {form.endDate || "-"}
          </td>
        </tr>
        <tr>
          <th>Budget</th>
          <td>₹{Number(form.budget || 0).toLocaleString("en-IN")}</td>
        </tr>
        <tr>
          <th>Landing Page</th>
          <td>{form.landingPage || "-"}</td>
        </tr>
        <tr>
          <th>Assets Selected</th>
          <td>{selectedAssetCount}</td>
        </tr>

        {form.platform === "Google" && (
          <>
            <tr>
              <th>Google Customer</th>
              <td>{form.google.customerId || "-"}</td>
            </tr>
            <tr>
              <th>Lead Form</th>
              <td>{form.google.leadFormAssetId || "-"}</td>
            </tr>
            <tr>
              <th>PMax Images</th>
              <td>
                Landscape {googleCounts.landscape}, Square{" "}
                {googleCounts.square}, Portrait {googleCounts.portrait},
                Logo {googleCounts.logo}
              </td>
            </tr>
          </>
        )}

        {form.platform === "Meta" && (
          <>
            <tr>
              <th>Meta Ad Account</th>
              <td>{form.meta.adAccountId || "-"}</td>
            </tr>
            <tr>
              <th>Facebook Page</th>
              <td>{form.meta.pageId || "-"}</td>
            </tr>
            <tr>
              <th>Lead Form</th>
              <td>{form.meta.leadFormId || "-"}</td>
            </tr>
          </>
        )}

        {form.platform === "YouTube" && (
          <tr>
            <th>YouTube Video</th>
            <td>{form.youtube.videoId || "-"}</td>
          </tr>
        )}
      </tbody>
    </Table>
  </div>
);

export default CampaignCreatePage;
